import { useCallback, useEffect, useState } from 'react'
import type { ChatMessage } from '../types'
import { MessageSourcesSchema } from '../types'

const STORAGE_KEY_PREFIX = 'scribbler-chat'

const VALID_ROLES = new Set<ChatMessage['role']>(['assistant', 'user'])
const VALID_STREAM_STATES = new Set<ChatMessage['streamState']>(['streaming', 'error', 'done'])

function isPersistedChatMessage(item: unknown): item is ChatMessage {
  if (typeof item !== 'object' || item === null) {
    return false
  }

  const candidate = item as Partial<ChatMessage>

  if (
    typeof candidate.id !== 'string' ||
    !VALID_ROLES.has(candidate.role as ChatMessage['role']) ||
    typeof candidate.content !== 'string' ||
    typeof candidate.timestamp !== 'number' ||
    !VALID_STREAM_STATES.has(candidate.streamState as ChatMessage['streamState'])
  ) {
    return false
  }

  if (
    candidate.streamPreviewContent !== undefined &&
    typeof candidate.streamPreviewContent !== 'string'
  ) {
    return false
  }

  // Optional ids for comment-navigation links; either string or null;null entries mean insert failed for that index.
  if (
    candidate.wordCommentIds !== undefined &&
    (!Array.isArray(candidate.wordCommentIds) ||
      candidate.wordCommentIds.some((id) => id !== null && typeof id !== 'string'))
  ) {
    return false
  }

  return true
}

// Messages saved before streaming was introduced lack streamState; treat them as 'done' on load.
interface LegacyPersistedChatMessage {
  id: string
  role: ChatMessage['role']
  content: string
  timestamp: number
}

function isLegacyPersistedChatMessage(item: unknown): item is LegacyPersistedChatMessage {
  if (typeof item !== 'object' || item === null) {
    return false
  }
  const candidate = item as Partial<LegacyPersistedChatMessage & ChatMessage>
  return (
    typeof candidate.id === 'string' &&
    VALID_ROLES.has(candidate.role as ChatMessage['role']) &&
    typeof candidate.content === 'string' &&
    typeof candidate.timestamp === 'number' &&
    !VALID_STREAM_STATES.has(candidate.streamState as ChatMessage['streamState'])
  )
}

function loadMessagesFromStorage(storageKey: string): ChatMessage[] {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return []

    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []

    const filtered = parsed.filter(
      (item): item is ChatMessage | LegacyPersistedChatMessage =>
        isPersistedChatMessage(item) || isLegacyPersistedChatMessage(item)
    )

    return filtered.map((item) => {
      const message: ChatMessage = isPersistedChatMessage(item)
        ? item
        : { ...item, streamState: 'done', streamPreviewContent: undefined }
      // Unreadable stored sources become undefined, which the UI shows as an error.
      let sources = message.sources
      if (sources !== undefined) {
        const parsedSources = MessageSourcesSchema.safeParse(sources)
        sources = parsedSources.success ? parsedSources.data : undefined
      }
      // Sanitize sendContext: drop it if it's not a plain object (e.g. stored as null)
      const sendContext =
        typeof message.sendContext === 'object' && message.sendContext !== null
          ? message.sendContext
          : undefined
      return { ...message, sendContext, sources }
    })
  } catch {
    return []
  }
}

export const useChatMessages = () => {
  const storageKey = STORAGE_KEY_PREFIX
  // Initialise synchronously so the first render already has the full history
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessagesFromStorage(storageKey))

  useEffect(() => {
    if (messages.some((msg) => msg.streamState === 'streaming')) {
      // Don't persist to storage if there are streaming messages, to avoid saving partial content on refresh
      return
    }
    if (messages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(messages))
    } else {
      localStorage.removeItem(storageKey)
    }
  }, [messages, storageKey])

  const addMessage = useCallback(
    (
      content: string,
      role: ChatMessage['role'] = 'user',
      streamState: ChatMessage['streamState'] = 'done',
      sendContext?: ChatMessage['sendContext']
    ) => {
      const newMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role,
        content,
        timestamp: Date.now(),
        streamState,
        sendContext,
        // Assistant messages start with no sources; only a failed read leaves this undefined.
        sources: role === 'assistant' ? [] : undefined,
      }
      setMessages((prevMessages) => [...prevMessages, newMessage])
      return newMessage.id
    },
    []
  )

  const updateMessage = useCallback(
    (
      id: string,
      updates: Partial<
        Pick<
          ChatMessage,
          | 'content'
          | 'streamState'
          | 'streamPreviewContent'
          | 'infoNote'
          | 'wordCommentIds'
          | 'telemetryTask'
          | 'sources'
        >
      >
    ) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg))
      )
    },
    []
  )

  const deleteMessage = useCallback((id: string) => {
    setMessages((prevMessages) => {
      const index = prevMessages.findIndex((msg) => msg.id === id)
      if (index === -1) return prevMessages
      // Also remove the immediately following assistant reply, if present.
      // Only targets the direct response — skips if the next message is another user message.
      const deleteCount = prevMessages[index + 1]?.role === 'assistant' ? 2 : 1
      return [...prevMessages.slice(0, index), ...prevMessages.slice(index + deleteCount)]
    })
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
    localStorage.removeItem(storageKey)
  }, [storageKey])

  return {
    messages,
    addMessage,
    updateMessage,
    deleteMessage,
    clearMessages,
  }
}
